// Bringing lit targets into view (presentation-2 contract 6.3). A decision that lights targets none of which can be seen, because the
// page or a pane that clips its content (a scene area with its own scrollbar) has them out of view, scrolls the first one into view once.

export interface Box { top: number; bottom: number; left: number; right: number }

function intersect(box: Box, clips: readonly Box[]): Box | null {
  let { top, bottom, left, right } = box;
  for (const clip of clips) {
    top = Math.max(top, clip.top); bottom = Math.min(bottom, clip.bottom);
    left = Math.max(left, clip.left); right = Math.min(right, clip.right);
  }
  return bottom > top && right > left ? { top, bottom, left, right } : null;
}

const area = (box: Box) => (box.bottom - box.top) * (box.right - box.left);
const degenerate = (box: Box) => !(box.bottom - box.top > 0) || !(box.right - box.left > 0);

/** The share (0..1) of `box` that lies inside every clip box. A degenerate box has none. */
export function visibleShare(box: Box, clips: readonly Box[]): number {
  if (degenerate(box)) return 0;
  const inside = intersect(box, clips);
  return inside ? area(inside) / area(box) : 0;
}

/**
 * Whether the box counts as seen: at least half of it is visible, or what is visible fills at least half of the room the clips leave
 * (a target taller than its pane that already fills the pane; scrolling it would only make the pane jump).
 */
export function isSeen(box: Box, clips: readonly Box[]): boolean {
  if (degenerate(box)) return false;
  const inside = intersect(box, clips);
  if (!inside) return false;
  if (area(inside) / area(box) >= 0.5) return true;
  const room = intersect(clips[0] ?? box, clips);
  return !!room && area(inside) / area(room) >= 0.5;
}

export interface OverflowFacts {
  overflowX: string; overflowY: string;
  scrollWidth: number; clientWidth: number; scrollHeight: number; clientHeight: number;
  /** The element being revealed lies partly outside the ancestor's visible (padding) box on that axis, including past its top or left
   * edge, which scrollWidth and scrollHeight never count. */
  escapesX?: boolean; escapesY?: boolean;
}

/**
 * What an ancestor does to its content. A scrolling pane (overflow auto or scroll) always bounds what is visible and is the only
 * kind the player can scroll, and only while its content overflows. A hidden or clip ancestor bounds what is visible only where its
 * content actually overflows (a clip that holds everything hides nothing; content past its top or left edge counts, which the scroll
 * sizes do not show), and the player cannot scroll it.
 */
export function classifyOverflow(f: OverflowFacts): { clips: boolean; scrollsX: boolean; scrollsY: boolean } {
  const userX = /^(auto|scroll|overlay)$/.test(f.overflowX), userY = /^(auto|scroll|overlay)$/.test(f.overflowY);
  const cutX = /^(hidden|clip)$/.test(f.overflowX), cutY = /^(hidden|clip)$/.test(f.overflowY);
  const overflowsX = f.scrollWidth > f.clientWidth + 1, overflowsY = f.scrollHeight > f.clientHeight + 1;
  return {
    clips: userX || userY || (cutX && (overflowsX || !!f.escapesX)) || (cutY && (overflowsY || !!f.escapesY)),
    scrollsX: userX && overflowsX,
    scrollsY: userY && overflowsY,
  };
}

/** Whether `box` reaches beyond `pane` on an axis (by more than a pixel of rounding). */
export function escapes(box: Box, pane: Box, axis: 'x' | 'y'): boolean {
  return axis === 'x' ? box.left < pane.left - 1 || box.right > pane.right + 1 : box.top < pane.top - 1 || box.bottom > pane.bottom + 1;
}

function boxOf(el: Element): Box {
  const r = el.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
}

/**
 * The visible area of a clipping or scrolling ancestor: its padding box (inside the borders, without scrollbars), which is what
 * clientWidth and clientHeight measure. The border box would count a scrollbar as room a target could be seen in.
 */
function paneBox(el: Element): Box {
  const r = el.getBoundingClientRect();
  if (!(el instanceof HTMLElement) || !el.offsetWidth || !el.offsetHeight) return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
  const scaleX = r.width / el.offsetWidth, scaleY = r.height / el.offsetHeight;
  const left = r.left + el.clientLeft * scaleX, top = r.top + el.clientTop * scaleY;
  return { top, bottom: top + el.clientHeight * scaleY, left, right: left + el.clientWidth * scaleX };
}

function overflowFacts(parent: Element, child: Element): OverflowFacts | null {
  // An SVG or other non-HTML ancestor has no layout box to overflow: it neither clips for this purpose nor scrolls.
  if (!(parent instanceof HTMLElement)) return null;
  const style = getComputedStyle(parent);
  const pane = paneBox(parent), box = boxOf(child);
  return {
    overflowX: style.overflowX, overflowY: style.overflowY,
    scrollWidth: parent.scrollWidth, clientWidth: parent.clientWidth, scrollHeight: parent.scrollHeight, clientHeight: parent.clientHeight,
    escapesX: escapes(box, pane, 'x'), escapesY: escapes(box, pane, 'y'),
  };
}

/** The viewport (above `floor`, the top of a docked prompt) and the visible area of every ancestor of `el` that clips its content. */
export function clipBoxes(el: Element, floor: number): Box[] {
  const clips: Box[] = [{ top: 0, bottom: floor, left: 0, right: window.innerWidth }];
  for (let parent = el.parentElement; parent && parent !== document.documentElement && parent !== document.body; parent = parent.parentElement) {
    const facts = overflowFacts(parent, el);
    if (facts && classifyOverflow(facts).clips) clips.push(paneBox(parent));
  }
  return clips;
}

export interface ScrollPane { box: Box; x: boolean; y: boolean }

function axisDelta(start: number, end: number, paneStart: number, paneEnd: number, block: 'start' | 'nearest' | 'center'): number {
  if (block === 'center') return (start + end) / 2 - (paneStart + paneEnd) / 2;
  if (block === 'start') return start - paneStart;
  if (start >= paneStart && end <= paneEnd) return 0;
  if (end - start > paneEnd - paneStart) return start - paneStart;
  return start < paneStart ? start - paneStart : end - paneEnd;
}

/**
 * How far each pane (innermost first) has to scroll, in order, to show `target`. Only panes the player can scroll appear here: a
 * container with overflow hidden or clip can be scrolled by script but not back by the player, so a reveal never touches it. Each
 * pane's move is taken into account for the next, outer one.
 */
export function planReveal(target: Box, panes: readonly ScrollPane[], block: 'start' | 'nearest' | 'center', inline: 'start' | 'nearest' | 'center' = 'nearest'): { dx: number; dy: number }[] {
  let { top, bottom, left, right } = target;
  return panes.map(pane => {
    const dy = pane.y ? axisDelta(top, bottom, pane.box.top, pane.box.bottom, block) : 0;
    const dx = pane.x ? axisDelta(left, right, pane.box.left, pane.box.right, inline) : 0;
    top -= dy; bottom -= dy; left -= dx; right -= dx;
    return { dx: Math.round(dx), dy: Math.round(dy) };
  });
}

/**
 * Scrolls `el` into view like `scrollIntoView`, but only through containers the player can scroll (and the page), never through an
 * overflow hidden or clip one, and with `floor` as the bottom of the viewport (a docked prompt).
 */
export function revealElement(el: Element, opts: { block: 'start' | 'nearest' | 'center'; behavior: ScrollBehavior; floor: number }): void {
  const scrolling: { node: Element | null; pane: ScrollPane }[] = [];
  for (let parent = el.parentElement; parent && parent !== document.documentElement && parent !== document.body; parent = parent.parentElement) {
    const facts = overflowFacts(parent, el);
    if (!facts) continue;
    const { scrollsX, scrollsY } = classifyOverflow(facts);
    if (scrollsX || scrollsY) scrolling.push({ node: parent, pane: { box: paneBox(parent), x: scrollsX, y: scrollsY } });
  }
  const root = document.scrollingElement;
  if (root && (root.scrollHeight > root.clientHeight || root.scrollWidth > root.clientWidth)) {
    scrolling.push({ node: null, pane: { box: { top: 0, bottom: opts.floor, left: 0, right: window.innerWidth }, x: root.scrollWidth > root.clientWidth, y: root.scrollHeight > root.clientHeight } });
  }
  const plan = planReveal(boxOf(el), scrolling.map(s => s.pane), opts.block);
  scrolling.forEach((s, i) => {
    const { dx, dy } = plan[i]!;
    if (!dx && !dy) return;
    if (s.node) s.node.scrollBy({ left: dx, top: dy, behavior: opts.behavior });
    else window.scrollBy({ left: dx, top: dy, behavior: opts.behavior });
  });
}

/** Whether the element counts as seen (see `isSeen`). */
export function mostlyVisible(el: Element, floor: number): boolean {
  return isSeen(el.getBoundingClientRect(), clipBoxes(el, floor));
}

/** The height a target has to be shown in: that of the smallest pane or viewport holding it. */
export function roomHeight(el: Element, floor: number): number {
  return Math.min(...clipBoxes(el, floor).map(c => c.bottom - c.top));
}

/**
 * Whether a wide scene's lit targets start a decision worth revealing: something is lit that was not lit at the last observation.
 * The same decision observed again (the same targets, or fewer of them as picks are made) never re-reveals, so a pane the player
 * scrolled away from the cards stays where it is.
 */
export function startsNewDecision(previous: ReadonlySet<string>, lit: readonly string[]): boolean {
  return lit.some(anchor => !previous.has(anchor));
}

/** The parts of a choice descriptor that make it the same pending choice (see `decisionIdentity`). */
export interface IdentifiedChoice { id: string; scope?: string; action: { kind: string; data?: unknown }; steps?: unknown }

// A small, stable string hash (cyrb53), so a key holds a short token rather than every descriptor.
function hash(text: string): string {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761); h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

// JSON with object members in sorted order: member order is not part of a descriptor's meaning (array order is).
function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') {
    const object = value as Record<string, unknown>;
    return `{${Object.keys(object).filter(k => object[k] !== undefined).sort().map(k => `${JSON.stringify(k)}:${canonical(object[k])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

const choiceSignature = (c: IdentifiedChoice) => `${c.id}|${c.scope ?? 'decision'}|${c.action.kind}|${hash(canonical({ data: c.action.data, steps: c.steps }))}`;

/**
 * The identity of the decision the player is deciding, for the reveal. The server's decision id is `matchId:revision`, so it names one
 * observation and changes whenever anyone does anything; it cannot say whether the decision itself is a new one. This does: it is
 * derived from the descriptors of the choices the player is deciding among (id, kind, data and steps, in no particular order), and so
 * it stays the same while the same pending decision is observed again across revisions, and changes when the choice set does.
 *
 * - With a draft open the identity is that draft's own choice, whatever else is on offer: answering its steps, an auxiliary draft
 *   carried across revisions, or a draft kept when a peer locks a simultaneous decision all stay one decision.
 * - Otherwise it is the set of decision choices (auxiliary choices come and go with the position and are not part of it).
 * - Nothing to decide has no identity (`null`).
 */
export function decisionIdentity(active: IdentifiedChoice | null | undefined, eligible: readonly IdentifiedChoice[]): string | null {
  if (active) return `draft|${choiceSignature(active)}`;
  const decision = eligible.filter(c => c.scope !== 'auxiliary').map(choiceSignature).sort();
  return decision.length > 0 ? `choices|${decision.length}|${hash(decision.join('\n'))}` : null;
}

/**
 * Whether a lit-target observation asks for a reveal on a wide scene. `decision` is a `decisionIdentity`, never the server's per
 * observation decision id. A new decision (a different identity) always does, whatever the player scrolled during the one before.
 * Within the same decision only targets that were not lit before do, and not after the player has scrolled since the last reveal;
 * the same targets observed again, or fewer of them, never do. A new layout width does.
 */
export function shouldReveal(input: { previousLit: ReadonlySet<string>; lit: readonly string[]; previousDecision: string | null | undefined; decision: string | null | undefined; relaid: boolean; scrolledSinceReveal: boolean }): boolean {
  if (input.relaid) return true;
  if (input.previousDecision !== input.decision) return input.lit.length > 0;
  if (!startsNewDecision(input.previousLit, input.lit)) return false;
  return !(input.previousLit.size > 0 && input.scrolledSinceReveal);
}

const TEXT_ENTRY = /^(INPUT|TEXTAREA|SELECT)$/;
const ACTIVATES = /^(BUTTON|A|SUMMARY|INPUT|TEXTAREA|SELECT)$/;
// Widgets that take Space to activate and the arrow keys to move between their items (roving focus), never to scroll.
const ACTIVATING_ROLES = new Set(['button', 'checkbox', 'option', 'radio', 'tab', 'menuitem', 'menuitemcheckbox', 'menuitemradio', 'switch', 'treeitem', 'link']);
const ARROW_ROLES = new Set(['slider', 'spinbutton', 'listbox', 'option', 'radio', 'radiogroup', 'tab', 'tablist', 'menu', 'menubar', 'menuitem', 'menuitemcheckbox', 'menuitemradio', 'switch', 'combobox', 'tree', 'treeitem', 'grid', 'gridcell', 'scrollbar']);

/**
 * Whether a key press scrolls something. Space on a button, link or a widget role (checkbox, radio, tab, menu item, switch) activates
 * it and never scrolls, and the arrow, page, home and end keys in a text field, a select or such a widget move the caret, the option
 * or the focus instead. Enter never scrolls.
 */
export function keyScrolls(key: string, target: { tagName?: string; isContentEditable?: boolean; role?: string | null } | null): boolean {
  const tag = (target?.tagName ?? '').toUpperCase();
  const editable = !!target?.isContentEditable || TEXT_ENTRY.test(tag);
  const role = (target?.role ?? '').trim().toLowerCase();
  if (key === ' ') return !(editable || ACTIVATES.test(tag) || ACTIVATING_ROLES.has(role));
  if (/^(Arrow|Page)/.test(key) || key === 'Home' || key === 'End') return !(editable || ARROW_ROLES.has(role));
  return false;
}
